import { Button, Form, Input, Modal, Select, Space, Tag } from "antd";
import { AimOutlined, DeleteOutlined, PlusOutlined } from "@ant-design/icons";
import { useState } from "react";
import { Category } from "../api/models";
import { useCategories } from "../swr/useCategories";
import { useAssemblyList } from "../swr/useAssemblyList";

export interface ChargeTargeting {
  category: string;
  bom?: string;
  filters: { field: string; value: string }[];
}

export const buildChargeTargeting = (t: ChargeTargeting | undefined): string | undefined => {
  if (!t?.category) return undefined;

  const expressions: any[] = [{ type: "category-has-selection", category: t.category }];
  if (t.bom) {
    expressions.push({
      type: "existing-selection-matches",
      lhs: { type: "assembly-attribute", attribute: "bom" },
      rhs: { type: "string", value: t.bom },
    });
  }
  t.filters.filter(f => f.field && f.value !== undefined && f.value !== "").forEach(f =>
    expressions.push({
      type: "equals",
      lhs: { type: "metadata", category: t.category, field: f.field },
      rhs: { type: "string", value: f.value },
    })
  );

  const condition = expressions.length === 1 ? expressions[0] : { type: "and", expressions };
  return JSON.stringify(condition);
};

//returns undefined for a condition this guided editor can't represent
export const parseChargeTargeting = (json: string | undefined | null): ChargeTargeting | undefined => {
  if (!json?.length) return { category: "", filters: [] };

  try {
    const condition = JSON.parse(json);
    const expressions = condition.type === "and" ? condition.expressions : [condition];
    if (!Array.isArray(expressions)) return undefined;

    const targeting: ChargeTargeting = { category: "", filters: [] };
    for (const expr of expressions) {
      if (expr.type === "category-has-selection" && !targeting.category) {
        targeting.category = expr.category;
      } else if (
        expr.type === "existing-selection-matches" &&
        expr.lhs?.type === "assembly-attribute" && expr.lhs?.attribute === "bom" &&
        expr.rhs?.type === "string" && !targeting.bom
      ) {
        targeting.bom = expr.rhs.value;
      } else if (
        expr.type === "equals" &&
        expr.lhs?.type === "metadata" && expr.lhs?.category && expr.lhs?.field &&
        expr.rhs?.type === "string"
      ) {
        targeting.filters.push({ field: expr.lhs.field, value: expr.rhs.value });
      } else {
        return undefined;
      }
    }
    return targeting.category ? targeting : undefined;
  } catch {
    return undefined;
  }
};

export const describeChargeTargeting = (json: string | undefined | null, categories?: Category[]): string => {
  if (!json?.length) return "All quotes";

  const targeting = parseChargeTargeting(json);
  if (!targeting) return "Custom condition";

  const categoryName = categories?.find(c => c.categoryId === targeting.category)?.name || targeting.category;
  const parts = [categoryName];
  if (targeting.bom) parts.push(targeting.bom);
  targeting.filters.forEach(f => parts.push(`${f.field} = ${f.value}`));
  return parts.join(", ");
};

const MetadataFilterRow = (props: {
  category: Category | undefined;
  filter: { field: string; value: string };
  onChange: (filter: { field: string; value: string }) => void;
  onRemove: () => void;
}) => {
  const { category, filter, onChange, onRemove } = props;

  const fieldDef = category?.metadata?.find(md => md.name === filter.field);
  const selectOptions = fieldDef?.fieldType === "select" ? fieldDef.selectOptions : undefined;

  return <Space>
    <Select
      showSearch
      style={{ width: "14rem" }}
      placeholder="Metadata field"
      value={filter.field || undefined}
      onChange={field => onChange({ field, value: "" })}
      options={category?.metadata?.filter(md => md.active !== false).map(md => ({ label: md.name, value: md.name }))}
    />
    <span>=</span>
    {selectOptions
      ? <Select
          showSearch
          style={{ width: "14rem" }}
          placeholder="Value"
          value={filter.value || undefined}
          onChange={value => onChange({ ...filter, value })}
          options={selectOptions.map(o => ({ label: o, value: o }))}
        />
      : <Input
          style={{ width: "14rem" }}
          placeholder="Value"
          value={filter.value}
          onChange={e => onChange({ ...filter, value: e.target.value })}
        />}
    <Button icon={<DeleteOutlined />} type="text" danger size="small" onClick={onRemove} />
  </Space>;
};

//separate component so the assembly list is only fetched once a category is chosen
const AssemblySelect = (props: {
  categoryId: string;
  bom: string | undefined;
  onChange: (bom: string | undefined) => void;
}) => {
  const assemblyListAsync = useAssemblyList({
    filterOptions: { categoryIdStr: props.categoryId },
    pageSize: 1000,
  });
  const assemblies = assemblyListAsync.data?.content;

  return <Select
    showSearch
    allowClear
    loading={assemblyListAsync.isLoading}
    placeholder="Any assembly in this category"
    value={props.bom}
    onChange={props.onChange}
    filterOption={(input, option) => (option?.label?.toString() ?? "").toLowerCase().includes(input.toLowerCase())}
    options={assemblies?.map(a => ({ label: `${a.bom} - ${a.bomDescription || a.label || ""}`, value: a.bom }))}
  />;
};

const SupplementalChargeTargetingModal = (props: {
  open: boolean;
  conditionDefinition: string | undefined;
  onSave: (conditionDefinition: string | undefined) => void;
  onCancel: () => void;
}) => {
  const { open, conditionDefinition, onSave, onCancel } = props;

  const initial = parseChargeTargeting(conditionDefinition);
  const unsupported = initial === undefined;
  const [targeting, setTargeting] = useState<ChargeTargeting>(initial || { category: "", filters: [] });

  const categoriesAsync = useCategories();
  const categories = categoriesAsync.data || [];
  const category = categories.find(c => c.categoryId === targeting.category);

  const handleSave = () => {
    onSave(buildChargeTargeting(targeting));
  };

  if (unsupported) {
    return <Modal
      title="Charge Targeting"
      open={open}
      onCancel={onCancel}
      footer={[
        <Button key="clear" danger onClick={() => onSave(undefined)}>Clear targeting</Button>,
        <Button key="close" onClick={onCancel}>Close</Button>,
      ]}
    >
      <p>This charge has a condition that can't be edited here.</p>
      <Input.TextArea readOnly rows={6} value={conditionDefinition || ""} />
    </Modal>;
  }

  return <Modal
    title="Charge Targeting"
    open={open}
    onOk={handleSave}
    okText="Save"
    okButtonProps={{ disabled: !!targeting.category && targeting.filters.some(f => !f.field || !f.value) }}
    onCancel={onCancel}
    width={640}
  >
    <p>Only apply this charge when the quote matches:</p>
    <Form layout="vertical">
      <Form.Item label="Category">
        <Select
          showSearch
          allowClear
          loading={categoriesAsync.isLoading}
          placeholder="All quotes (no targeting)"
          value={targeting.category || undefined}
          onChange={categoryId => setTargeting({ category: categoryId || "", filters: [] })}
          filterOption={(input, option) => (option?.label?.toString() ?? "").toLowerCase().includes(input.toLowerCase())}
          options={categories.map(c => ({ label: c.name, value: c.categoryId }))}
        />
      </Form.Item>
      {!!targeting.category && <>
        <Form.Item label="Assembly (optional)">
          <AssemblySelect
            categoryId={targeting.category}
            bom={targeting.bom}
            onChange={bom => setTargeting({ ...targeting, bom })}
          />
        </Form.Item>
        <Form.Item label={<>Metadata filters (all must match)<Button style={{ marginLeft: ".5rem" }} icon={<PlusOutlined />} type="primary" shape="circle" size="small" onClick={() => setTargeting({ ...targeting, filters: [...targeting.filters, { field: "", value: "" }] })} /></>}>
          <Space direction="vertical">
            {targeting.filters.map((filter, ndx) =>
              <MetadataFilterRow
                key={"filter-" + ndx}
                category={category}
                filter={filter}
                onChange={f => setTargeting({ ...targeting, filters: targeting.filters.map((v, i) => i === ndx ? f : v) })}
                onRemove={() => setTargeting({ ...targeting, filters: targeting.filters.filter((_v, i) => i !== ndx) })}
              />)}
          </Space>
        </Form.Item>
      </>}
    </Form>
  </Modal>;
};

//antd Form.Item compatible (value/onChange injected)
export const ChargeTargetingButton = (props: {
  value?: string | null;
  onChange?: (conditionDefinition: string | null) => void;
}) => {
  const conditionDefinition = props.value || undefined;
  const [open, setOpen] = useState<boolean>(false);

  const categoriesAsync = useCategories();
  const targeted = !!conditionDefinition?.length;

  return <>
    <Tag
      icon={<AimOutlined />}
      color={targeted ? "blue" : undefined}
      style={{ cursor: "pointer" }}
      onClick={() => setOpen(true)}
    >
      {describeChargeTargeting(conditionDefinition, categoriesAsync.data)}
    </Tag>
    {open &&
      <SupplementalChargeTargetingModal
        open={open}
        conditionDefinition={conditionDefinition}
        onSave={cd => { props.onChange?.(cd ?? null); setOpen(false); }}
        onCancel={() => setOpen(false)}
      />}
  </>;
};
